<template>
  <nav
    class="overflow-hidden rounded-b-3xl"
    :class="{
      'border-b border-outline-dividers bg-cascade-navigation': isScrolled,
      'bg-cascade-header': !isScrolled,
    }"
  >
    <div
      class="flex h-[52px] lg:h-20 items-center py-3 lg:py-5 pr-2 lg:pr-5 lg:pl-2 justify-between max-w-[1440px] mx-auto"
    >
      <div class="flex items-center">
        <!-- PHX LOGO -->
        <IconPHXLogo
          @click="router.push('/dashboard')"
          class="h-8 lg:mr-[2rem] cursor-pointer"
          :class="UIStore.is_dark_mode ? 'text-white' : 'text-black'"
        />
        <MobileNavLinks />
        <!-- Router Links -->
        <div class="items-center hidden lg:flex gap-2">
          <RouterLink
            v-for="nav_route in navItems"
            :to="nav_route.path"
            class="flex justify-center h-10 items-center rounded-[117px] font-medium"
            :class="nav_route.path === route.path ? 'bg-tab-bg-selected font-semibold' : ''"
            @click="navItemClicked(nav_route.name)"
          >
            <p
              class="text-strongest font-inter p-3 text-sm"
              :class="nav_route.path === route.fullPath ? 'font-semibold' : 'font-medium'"
            >
              {{ nav_route.name }}
            </p>
          </RouterLink>
        </div>
      </div>

      <!-- Call Us + Name + Logout CTA -->
      <div v-if="dataStore.contact" class="flex gap-1 lg:gap-4 items-center">
        <Button
          @click="handleCallClick"
          size="sm"
          variant="paper-primary"
          class="hidden xl:inline-flex"
        >
          <template #label>
            <span class="font-inter">Call 855-732-6395</span>
          </template>
          <template #icon>
            <Icon name="Phone" class="h-4 w-4" :stroke-width="1.5" />
          </template>
        </Button>
        <Button
          @click="handleCallClick"
          size="xs"
          variant="paper-primary"
          class="hidden md:flex xl:hidden"
        >
          <template #label>
            <span class="font-inter">Call Us</span>
          </template>
        </Button>
        <Button @click="handleCallClick" size="xs" variant="paper-primary" class="md:hidden">
          <template #icon>
            <Icon name="Phone" class="h-4 w-4" :stroke-width="1.5" />
          </template>
        </Button>
        <p class="hidden lg:block text-strongest text-sm font-inter font-medium whitespace-nowrap">
          {{ displayedInvestorName }}
        </p>

        <div class="flex items-center gap-1">
          <Button
            size="xs"
            variant="paper-primary"
            @click="handleThemeToggle"
            class="hidden lg:inline-flex"
          >
            <template #icon>
              <Icon
                :name="UIStore.is_dark_mode ? 'Sun' : 'Moon'"
                class="h-4 w-4"
                :stroke-width="2"
              />
            </template>
          </Button>
          <Menu :actions="actionsList" position="right-0" class="mt-1">
            <template #button-content>
              <Button size="xs" variant="paper-primary">
                <template #icon>
                  <Icon name="User" class="h-4 w-4" :stroke-width="2" />
                </template>
              </Button>
            </template>
          </Menu>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from "vue";
import { useRouter, useRoute } from "vue-router";

import { Icon, Menu, Button, MobileNavLinks } from "@/components";
import { IconPHXLogo } from "@/components/icons";
import { useAuthStore, useDataStore, useUIStore, useCrudStore } from "@/stores";
import { useAnalytics } from "@/composables/useAnalytics";

const router = useRouter();
const route = useRoute();

const authStore = useAuthStore();
const dataStore = useDataStore();
const UIStore = useUIStore();
const crudStore = useCrudStore();
const { callButtonClicked, themeToggled, logoutClicked, navItemClicked } = useAnalytics();

const displayedInvestorName = ref("");
const isScrolled = ref(false);
const windowWidth = ref(window.innerWidth);

let isScrollTicking = false;

onMounted(() => {
  window.addEventListener("scroll", handleScroll, { passive: true });
  window.addEventListener("resize", handleResize);

  handleScroll();
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
  window.removeEventListener("resize", handleResize);
});

function handleScroll() {
  // Sync the class toggle to the paint cycle instead of raw scroll events —
  // on slower devices, unthrottled updates can lag a frame behind the visible
  // scroll, letting content show through the still-transparent nav.
  if (isScrollTicking) return;
  isScrollTicking = true;
  requestAnimationFrame(() => {
    isScrolled.value = window.scrollY > 0;
    isScrollTicking = false;
  });
}

function handleResize() {
  windowWidth.value = window.innerWidth;
}

watch(
  () => dataStore.contact,
  (newContact) => {
    if (newContact) {
      displayedInvestorName.value = `${newContact.first_name} ${newContact.last_name}`;
    }
  },
  { immediate: true }
);

const navItems = ref([
  { name: "Dashboard", path: "/dashboard" },
  { name: "Our Offerings", path: "/make-an-investment" },
  { name: "Portfolio", path: "/portfolio" },
  { name: "Profile", path: "/profile" },
  { name: "Documents", path: "/documents" },
  { name: "Contact Us", path: "/contact-us" },
  // { name: "Newsfeed", path: "/newsfeed" },
]);

const actionsList = computed(() => {
  const actions = [
    {
      label: "Logout",
      icon: "LogOut",
      onClick: () => handleSignOut(),
    },
  ];

  // lg breakpoint is typically 1024px in Tailwind
  if (windowWidth.value < 1024) {
    actions.push({
      label: UIStore.is_dark_mode ? "Light Mode" : "Dark Mode",
      icon: UIStore.is_dark_mode ? "Sun" : "Moon",
      onClick: () => handleThemeToggle(),
    });
  }

  return [actions];
});

async function handleSignOut() {
  logoutClicked();
  await authStore.signOut();
  await router.push("/sign-in");
  authStore.resetApplication();
}

const handleCallClick = () => {
  callButtonClicked(route.path);

  let log = {
    step: "Call Us",
    substep: "tel:855-732-6395",
    status: "Navigated",
    message: "",
    url: "",
    validation_details: null,
  };

  log = dataStore.setDefaultFields(log, "investoractivity");
  crudStore.insert("investoractivity", [log]);

  window.location.href = "tel:855-732-6395";
};

function handleThemeToggle() {
  themeToggled(UIStore.is_dark_mode ? "light" : "dark");
  UIStore.toggleDarkMode();
}
</script>
